import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, Navigate } from "react-router-dom";
import { Loader2, ShoppingBag, Users, Mail, MessageSquare, Trophy, Coins, ArrowLeft } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/hooks/useAuth";

import { API } from "@/lib/api";

const TABS = [
  { id: "stats", label: "Statystyki", icon: Coins },
  { id: "orders", label: "Zamówienia", icon: ShoppingBag },
  { id: "users", label: "Użytkownicy", icon: Users },
  { id: "newsletter", label: "Newsletter", icon: Mail },
  { id: "contact", label: "Wiadomości", icon: MessageSquare },
  { id: "quiz", label: "Quiz", icon: Trophy },
];

function StatCard({ label, value, accent }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      className="rounded-2xl bg-white border border-[#BAE6FD] p-6"
      style={{ borderTopColor: accent, borderTopWidth: 4 }}
    >
      <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#0b2a55]/60">{label}</div>
      <div className="mt-2 font-display font-semibold text-[#051937] text-3xl">{value}</div>
    </motion.div>
  );
}

export default function AdminPage() {
  const { user, loading: authLoading, logout } = useAuth();
  const [tab, setTab] = useState("stats");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    (async () => {
      setLoading(true);
      try {
        if (tab === "stats") {
          const r = await axios.get(`${API}/admin/stats`);
          setData({ stats: r.data });
        } else {
          const r = await axios.get(`${API}/admin/${tab}`);
          setData({ [tab]: r.data });
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [tab, user]);

  if (authLoading) {
    return (
      <div className="min-h-screen grid place-items-center">
        <Loader2 className="animate-spin text-[#60A5FA]" size={40} />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: "/admin" }} />;
  if (user.role !== "admin")
    return (
      <div className="min-h-screen pt-40 max-w-lg mx-auto px-6 text-center" data-testid="admin-forbidden">
        <h1 className="font-display font-semibold text-4xl text-[#051937]">To miejsce jest tylko dla administratora ✨</h1>
        <Link to="/" className="inline-flex items-center gap-2 mt-8 rounded-full bg-[#051937] text-white px-6 py-3 font-semibold">
          <ArrowLeft size={16} /> Strona główna
        </Link>
      </div>
    );

  const s = data.stats || {};

  return (
    <div className="min-h-screen pt-28 md:pt-32 pb-20 relative" data-testid="admin-page">
      <div className="aurora" style={{ width: 480, height: 480, background: "#FDE68A", top: "5%", left: "-8%" }} />

      <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <span className="chip"><span className="dot" /> Panel administratora</span>
            <h1 className="font-display font-semibold text-[#051937] text-3xl md:text-4xl mt-3 leading-tight">
              Witaj, <em className="not-italic text-[#60A5FA]">{user.name || "Admin"}</em>
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="chip !bg-white hover:!bg-[#F4FAFF]" data-testid="admin-home">
              <ArrowLeft size={12} /> Sklep
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 rounded-full bg-white border border-[#FECACA] text-[#DC2626] px-4 py-2 text-xs font-bold uppercase tracking-[0.16em]"
            >
              Wyloguj
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-10 flex flex-wrap gap-2">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs uppercase tracking-[0.16em] font-bold border-2 transition-all ${
                  active
                    ? "bg-[#051937] text-white border-[#051937]"
                    : "bg-white text-[#051937] border-[#BAE6FD] hover:border-[#60A5FA]"
                }`}
              >
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </div>

        {loading && (
          <div className="mt-16 grid place-items-center">
            <Loader2 className="animate-spin text-[#60A5FA]" size={32} />
          </div>
        )}

        {!loading && tab === "stats" && (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Wszystkie zamówienia" value={s.total_orders ?? 0} accent="#60A5FA" />
            <StatCard label="Opłacone" value={s.paid_orders ?? 0} accent="#10B981" />
            <StatCard label="Przychód (PLN)" value={(s.total_revenue_pln ?? 0).toFixed(2)} accent="#FBBF24" />
            <StatCard label="Użytkownicy" value={s.total_users ?? 0} accent="#8B5CF6" />
            <StatCard label="Newsletter" value={s.newsletter_subs ?? 0} accent="#F472B6" />
            <StatCard label="Wiadomości" value={s.contact_msgs ?? 0} accent="#F87171" />
            <StatCard label="Wyniki quizu" value={s.quiz_scores ?? 0} accent="#22D3EE" />
          </div>
        )}

        {!loading && tab === "orders" && (
          <div className="mt-8 rounded-2xl border border-[#BAE6FD] bg-white overflow-x-auto" data-testid="admin-orders-table">
            <table className="min-w-full text-sm">
              <thead className="bg-[#F4FAFF] text-[#0b2a55]/70">
                <tr>
                  <th className="text-left px-4 py-3 uppercase tracking-[0.14em] text-xs">Data</th>
                  <th className="text-left px-4 py-3 uppercase tracking-[0.14em] text-xs">Email</th>
                  <th className="text-left px-4 py-3 uppercase tracking-[0.14em] text-xs">Kwota</th>
                  <th className="text-left px-4 py-3 uppercase tracking-[0.14em] text-xs">Status</th>
                  <th className="text-left px-4 py-3 uppercase tracking-[0.14em] text-xs">Session</th>
                </tr>
              </thead>
              <tbody>
                {(data.orders || []).map((o) => (
                  <tr key={o.session_id} className="border-t border-[#BAE6FD]/60">
                    <td className="px-4 py-3 text-[#0b2a55]/80">{(o.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-4 py-3">{o.email || "—"}</td>
                    <td className="px-4 py-3 font-semibold">{((o.amount || 0) / 100).toFixed(2)} {(o.currency || "").toUpperCase()}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block px-2.5 py-1 rounded-full text-xs font-bold ${
                          o.payment_status === "paid"
                            ? "bg-[#DCFCE7] text-[#166534]"
                            : "bg-[#FEF3C7] text-[#92400E]"
                        }`}
                      >
                        {o.payment_status}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-[#0b2a55]/70">{o.session_id.slice(-12)}</td>
                  </tr>
                ))}
                {(data.orders || []).length === 0 && (
                  <tr><td colSpan="5" className="px-4 py-8 text-center text-[#0b2a55]/60">Brak zamówień</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {!loading && tab === "users" && (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-users-list">
            {(data.users || []).map((u) => (
              <div key={u.user_id} className="rounded-2xl bg-white border border-[#BAE6FD] p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-display font-semibold text-[#051937]">{u.name || u.email}</div>
                    <div className="text-xs text-[#0b2a55]/60">{u.email}</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-[0.16em] font-bold px-2 py-1 rounded-full ${u.role === "admin" ? "bg-[#FDE68A] text-[#051937]" : "bg-[#F4FAFF] text-[#0b2a55]"}`}>
                    {u.role}
                  </span>
                </div>
              </div>
            ))}
            {(data.users || []).length === 0 && (
              <p className="col-span-full text-center text-[#0b2a55]/60 py-8">Brak użytkowników</p>
            )}
          </div>
        )}

        {!loading && tab === "newsletter" && (
          <div className="mt-8 rounded-2xl border border-[#BAE6FD] bg-white p-6" data-testid="admin-newsletter-list">
            <ul className="divide-y divide-[#BAE6FD]/60">
              {(data.newsletter || []).map((n, i) => (
                <li key={i} className="py-3 flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-[#051937]">{n.email}</div>
                    {n.name && <div className="text-xs text-[#0b2a55]/60">{n.name}</div>}
                  </div>
                  <span className="text-xs text-[#0b2a55]/60">{(n.created_at || "").slice(0, 10)}</span>
                </li>
              ))}
              {(data.newsletter || []).length === 0 && (
                <p className="text-center text-[#0b2a55]/60 py-4">Brak zapisów</p>
              )}
            </ul>
          </div>
        )}

        {!loading && tab === "contact" && (
          <div className="mt-8 space-y-4" data-testid="admin-contact-list">
            {(data.contact || []).map((m) => (
              <div key={m.id} className="rounded-2xl bg-white border border-[#BAE6FD] p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-display font-semibold text-[#051937]">{m.name}</div>
                    <div className="text-xs text-[#0b2a55]/60">{m.email}</div>
                  </div>
                  <span className="text-xs text-[#0b2a55]/60">{(m.created_at || "").slice(0, 10)}</span>
                </div>
                <p className="mt-3 text-[#0b2a55]/85">{m.message}</p>
              </div>
            ))}
            {(data.contact || []).length === 0 && (
              <p className="text-center text-[#0b2a55]/60 py-8">Brak wiadomości</p>
            )}
          </div>
        )}

        {!loading && tab === "quiz" && (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-quiz-list">
            {(data.quiz || []).map((q, i) => (
              <div key={i} className="rounded-2xl bg-white border border-[#BAE6FD] p-5">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-[#FDE68A] grid place-items-center text-[#051937]">
                    <Trophy size={18} />
                  </span>
                  <div>
                    <div className="font-display font-semibold text-[#051937]">
                      {q.score}/{q.total}
                    </div>
                    <div className="text-xs text-[#0b2a55]/60">{q.name || "Anonim"}</div>
                  </div>
                </div>
                <div className="mt-3 text-xs text-[#0b2a55]/60">{(q.created_at || "").slice(0, 16).replace("T", " ")}</div>
              </div>
            ))}
            {(data.quiz || []).length === 0 && (
              <p className="col-span-full text-center text-[#0b2a55]/60 py-8">Brak wyników</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
